
// ============================================================
// WaterOrderView — Mobile-First Order Form for "Order & Deliver → Drinking Water"
//
// Flow: Provider Selection (AHLA Bottled | ZAMZAM Refill)
//   → Product cards with smart MOQ steppers
//   → Cart summary with MOQ compliance
//   → Place Order (api.jobs.create, mode=order_deliver, parcelCategory=water)
//
// MOQ constraint (critical): EVERY product has a Minimum Order Quantity of 5.
//   AHLA: 18.9L Gallon, 24 Pack of 750ml, 6 Pack of 2 liter — all MOQ 5
//   ZAMZAM: 18.9L Refill Gallon — MOQ 5
//
// Smart Stepper behaviour:
//   - Starts at 0 (nothing in cart).
//   - First "+" click snaps to MOQ (5) and toasts "Adjusted to minimum (5)".
//   - "-" disabled at 0; once started, "-" disabled at MOQ (cannot drop below).
//   - Inline MOQ badge + bulk-discount upsell at qty ≥ 10.
// ============================================================

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/features/states";
import { currencySymbol, formatPrice } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Droplets,
  Minus,
  Plus,
  Star,
  Clock,
  Truck,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  PackageCheck,
  ShoppingCart,
  Sparkles,
  Phone,
  StickyNote,
  ChevronRight,
  Info,
} from "lucide-react";

// ============================================================
// Product Catalog
// ============================================================

type WaterProviderId = "ahla" | "zamzam";

interface WaterProduct {
  id: string;
  name: string;
  shortName: string;
  description: string;
  unitPrice: number; // in local currency units (Fdj for Djibouti launch market)
  minOrderQty: number; // MOQ — 5 for every product
  maxOrderQty: number;
  unit: string; // "gallon" | "pack"
  packSize?: string; // "24 bottles" | "6 bottles"
  icon: "gallon" | "pack-small" | "pack-large" | "refill";
}

interface WaterProvider {
  id: WaterProviderId;
  name: string;
  tagline: string;
  type: "bottled" | "refill";
  rating: number;
  ratingCount: number;
  estMinutes: string;
  deliveryFee: number;
  color: string; // tailwind gradient classes
  accent: string; // tailwind text/border accent
  logo: string;
  blurb: string;
  products: WaterProduct[];
}

// Prices are in the launch-market currency (Fdj for Djibouti).
// currencySymbol() adapts the symbol for Somalia (Sh) / Ethiopia (ETB).
const PROVIDERS: Record<WaterProviderId, WaterProvider> = {
  ahla: {
    id: "ahla",
    name: "AHLA Water",
    tagline: "Packaged Bottled Water",
    type: "bottled",
    rating: 4.8,
    ratingCount: 120,
    estMinutes: "20-30 min",
    deliveryFee: 200,
    color: "from-sky-50 to-sky-100 dark:from-sky-950/30 dark:to-sky-900/20",
    accent: "text-sky-600 dark:text-sky-400",
    logo: "/water/ahla-logo.png",
    blurb: "Premium sealed bottled water in three convenient pack formats.",
    products: [
      {
        id: "ahla-750-24",
        name: "750ml Bottled Water",
        shortName: "750ml × 24",
        description: "Case of 24 sealed 750ml bottles — perfect for offices & events.",
        unitPrice: 1200,
        minOrderQty: 5,
        maxOrderQty: 40,
        unit: "pack",
        packSize: "24 bottles",
        icon: "pack-small",
      },
      {
        id: "ahla-2l-6",
        name: "2-Liter Bottled Water",
        shortName: "2L × 6",
        description: "Case of 6 sealed 2-liter bottles — family daily use.",
        unitPrice: 900,
        minOrderQty: 5,
        maxOrderQty: 30,
        unit: "pack",
        packSize: "6 bottles",
        icon: "pack-large",
      },
      {
        id: "ahla-18.9",
        name: "18.9-Liter Gallon",
        shortName: "18.9L Gallon",
        description: "Large dispenser gallon of sealed bottled water.",
        unitPrice: 500,
        minOrderQty: 5,
        maxOrderQty: 20,
        unit: "gallon",
        icon: "gallon",
      },
    ],
  },
  zamzam: {
    id: "zamzam",
    name: "ZAMZAM Water",
    tagline: "Refill Purified Water",
    type: "refill",
    rating: 4.9,
    ratingCount: 85,
    estMinutes: "15-25 min",
    deliveryFee: 250,
    color: "from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20",
    accent: "text-emerald-600 dark:text-emerald-400",
    logo: "/water/zamzam-logo.png",
    blurb: "Eco-friendly refill service — we refill your empty 18.9L gallons.",
    products: [
      {
        id: "zamzam-18.9-refill",
        name: "18.9-Liter Refill Gallon",
        shortName: "18.9L Refill",
        description: "Refilled purified water for your 18.9L dispenser gallon. Have empty gallons ready for exchange.",
        unitPrice: 450,
        minOrderQty: 5,
        maxOrderQty: 25,
        unit: "gallon",
        icon: "refill",
      },
    ],
  },
};

const SERVICE_FEE_RATE = 0.05; // 5% consumer platform service fee
const BULK_TIERS = [
  { qty: 20, discountPercent: 10 },
  { qty: 10, discountPercent: 5 },
];

// Bulk-discount lookup: returns the best applicable tier for a quantity.
function bulkDiscount(qty: number): { discountPercent: number } | null {
  for (const tier of BULK_TIERS) {
    if (qty >= tier.qty) return tier;
  }
  return null;
}

// ============================================================
// Product icon (inline SVG to avoid extra image assets)
// ============================================================

function ProductIcon({ type, className }: { type: WaterProduct["icon"]; className?: string }) {
  const c = className ?? "h-8 w-8";
  switch (type) {
    case "gallon":
      return (
        <svg viewBox="0 0 48 48" className={c} fill="none" aria-hidden="true">
          <path d="M18 6h12v6h3a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H15a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3h3V6Z" fill="currentColor" opacity="0.15" />
          <path d="M18 6h12v6h3a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H15a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3h3V6Z" stroke="currentColor" strokeWidth="2" />
          <path d="M16 22h16M16 30h16" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
        </svg>
      );
    case "refill":
      return (
        <svg viewBox="0 0 48 48" className={c} fill="none" aria-hidden="true">
          <path d="M18 6h12v6h3a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H15a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3h3V6Z" fill="currentColor" opacity="0.15" />
          <path d="M18 6h12v6h3a3 3 0 0 1 3 3v27a3 3 0 0 1-3 3H15a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3h3V6Z" stroke="currentColor" strokeWidth="2" />
          <path d="M30 24a6 6 0 0 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M30 24v-3m0 3h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "pack-small":
      return (
        <svg viewBox="0 0 48 48" className={c} fill="none" aria-hidden="true">
          <rect x="6" y="14" width="36" height="28" rx="2" fill="currentColor" opacity="0.15" />
          <rect x="6" y="14" width="36" height="28" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M6 22h36M14 14v-4h6v4M28 14v-4h6v4" stroke="currentColor" strokeWidth="2" />
          <path d="M16 30h4M24 30h4M32 30h4M16 36h4M24 36h4M32 36h4" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
        </svg>
      );
    case "pack-large":
      return (
        <svg viewBox="0 0 48 48" className={c} fill="none" aria-hidden="true">
          <rect x="8" y="16" width="32" height="26" rx="2" fill="currentColor" opacity="0.15" />
          <rect x="8" y="16" width="32" height="26" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M8 24h32M16 16v-5h6v5M26 16v-5h6v5" stroke="currentColor" strokeWidth="2" />
          <path d="M14 32h6M28 32h6" stroke="currentColor" strokeWidth="2" opacity="0.6" />
        </svg>
      );
  }
}

// ============================================================
// Smart Stepper
// ============================================================

interface StepperProps {
  qty: number;
  minQty: number; // MOQ
  maxQty: number;
  onChange: (next: number) => void;
  accent: string; // tailwind text accent
}

function SmartStepper({ qty, minQty, maxQty, onChange, accent }: StepperProps) {
  const { t } = useTranslation();
  const atZero = qty === 0;
  const atMin = qty > 0 && qty <= minQty;
  const atMax = qty >= maxQty;

  // First "+" snaps from 0 → MOQ (Rule A: "Add to Cart" block, auto-adjust).
  const increment = () => {
    if (atMax) return;
    if (qty === 0) {
      onChange(minQty);
      toast.info(t("waterOrder.adjustedToMin", { min: minQty }));
      return;
    }
    onChange(qty + 1);
  };

  const decrement = () => {
    if (atZero) return;
    if (qty <= minQty) {
      // Rule A: disabled at MOQ — drop to 0 (clear the line) rather than below MOQ.
      onChange(0);
      toast.info(t("waterOrder.removedBelowMin"));
      return;
    }
    onChange(qty - 1);
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={decrement}
        disabled={atZero}
        aria-label={t("waterOrder.decreaseQty")}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
      >
        <Minus className="h-4 w-4" />
      </button>
      <div
        className={`flex h-10 w-14 items-center justify-center rounded-lg border-2 font-bold tabular-nums transition ${
          qty > 0
            ? `border-current ${accent} bg-current/5`
            : "border-border text-muted-foreground"
        }`}
      >
        {qty}
      </div>
      <button
        type="button"
        onClick={increment}
        disabled={atMax}
        aria-label={t("waterOrder.increaseQty")}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================
// Product Card
// ============================================================

interface ProductCardProps {
  product: WaterProduct;
  qty: number;
  accent: string;
  onQtyChange: (next: number) => void;
}

function ProductCard({ product, qty, accent, onQtyChange }: ProductCardProps) {
  const { t } = useTranslation();
  const tier = bulkDiscount(qty);
  const lineTotal = product.unitPrice * qty;
  const nextTier = useMemo(() => {
    // Nearest (lowest-qty) bulk tier the user hasn't reached yet.
    return [...BULK_TIERS]
      .sort((a, b) => a.qty - b.qty)
      .find((T) => qty < T.qty) ?? null;
  }, [qty]);

  return (
    <Card
      className={`overflow-hidden transition ${
        qty > 0 ? "border-primary/40 shadow-sm" : "border-border"
      }`}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Icon */}
          <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent.includes("sky") ? "from-sky-50 to-sky-100 dark:from-sky-950/40 dark:to-sky-900/20" : accent.includes("emerald") ? "from-emerald-50 to-emerald-100 dark:from-emerald-950/40 dark:to-emerald-900/20" : "from-muted to-muted/50"} ${accent}`}>
            <ProductIcon type={product.icon} className="h-9 w-9" />
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold leading-tight">{product.name}</p>
                {product.packSize && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("waterOrder.packOf", { size: product.packSize })}</p>
                )}
              </div>
              <Badge
                variant="outline"
                className={`shrink-0 border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300`}
              >
                <AlertTriangle className="mr-1 h-3 w-3" />
                {t("waterOrder.minOrder", { min: product.minOrderQty })}
              </Badge>
            </div>

            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{product.description}</p>

            <div className="mt-2 flex items-center gap-3">
              <span className={`text-lg font-bold ${accent}`}>
                {formatPrice(product.unitPrice)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("waterOrder.perUnit", { unit: product.unit })}
              </span>
            </div>
          </div>
        </div>

        {/* Stepper row */}
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
          <div className="min-w-0">
            {qty === 0 ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                {t("waterOrder.firstAddSetsToMin", { min: product.minOrderQty })}
              </p>
            ) : tier ? (
              <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <Sparkles className="h-3.5 w-3.5" />
                {t("waterOrder.bulkSaved", { pct: tier.discountPercent })}
              </p>
            ) : nextTier ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                {t("waterOrder.addMoreForBulk", { n: nextTier.qty - qty, pct: nextTier.discountPercent })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">&nbsp;</p>
            )}
          </div>
          <SmartStepper
            qty={qty}
            minQty={product.minOrderQty}
            maxQty={product.maxOrderQty}
            onChange={onQtyChange}
            accent={accent}
          />
        </div>

        {/* Line total + quick bulk-add nudge */}
        {qty > 0 && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {qty} × {formatPrice(product.unitPrice)}
              {tier ? ` − ${tier.discountPercent}%` : ""}
            </span>
            <span className={`text-sm font-bold ${accent}`}>
              {formatPrice(lineTotal - (tier ? (lineTotal * tier.discountPercent) / 100 : 0))}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Provider Selection Card
// ============================================================

function ProviderCard({ provider, onSelect }: { provider: WaterProvider; onSelect: () => void }) {
  const { t } = useTranslation();
  return (
    <Card
      className="group cursor-pointer overflow-hidden border-border transition hover:border-primary/50 hover:shadow-md"
      onClick={onSelect}
    >
      <div className={`relative flex h-24 items-center justify-center bg-gradient-to-br ${provider.color}`}>
        <img
          src={provider.logo}
          alt={provider.name}
          className="h-20 w-20 object-contain drop-shadow-sm"
          loading="lazy"
        />
        <Badge
          className={`absolute right-2 top-2 ${
            provider.type === "bottled"
              ? "bg-sky-600 text-white dark:bg-sky-700"
              : "bg-emerald-600 text-white dark:bg-emerald-700"
          }`}
        >
          <Droplets className="mr-1 h-3 w-3" />
          {provider.type === "bottled" ? t("waterOrder.bottled") : t("waterOrder.refill")}
        </Badge>
      </div>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-bold leading-tight">{provider.name}</p>
            <p className="text-xs text-muted-foreground">{provider.tagline}</p>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold">{provider.rating}</span>
            <span className="text-xs text-muted-foreground">({provider.ratingCount})</span>
          </div>
        </div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{provider.blurb}</p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {provider.estMinutes}
          </span>
          <span className="flex items-center gap-1">
            <Truck className="h-3.5 w-3.5" /> {formatPrice(provider.deliveryFee)}
          </span>
        </div>
        {/* MOQ warning row */}
        <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {provider.products.length === 1
              ? t("waterOrder.singleMoqWarning", { min: provider.products[0].minOrderQty })
              : t("waterOrder.multiMoqWarning", { min: provider.products[0].minOrderQty })}
          </span>
        </div>
        <Button className="w-full" size="sm">
          {t("waterOrder.select")}
          <ChevronRight className="ml-1 h-4 w-4 rtl-flip" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Main View
// ============================================================

export function WaterOrderView() {
  const { t } = useTranslation();
  const navigate = useUiStore((s) => s.navigate);
  const { user } = useAuthStore();
  const currency = currencySymbol(user?.countryCode);

  // Read preselected provider from sessionStorage (set by dashboard water tiles).
  const [providerId, setProviderId] = useState<WaterProviderId | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.sessionStorage.getItem("waterProvider");
    if (stored === "ahla" || stored === "zamzam") {
      window.sessionStorage.removeItem("waterProvider");
      return stored;
    }
    return null;
  });

  const provider = providerId ? PROVIDERS[providerId] : null;

  // Quantity per product id.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [dropoffNotes, setDropoffNotes] = useState("");
  const [contactPhone, setContactPhone] = useState(user?.phone || "");
  const [loading, setLoading] = useState(false);

  // Switch provider (or go back to the selection screen) and clear the cart
  // so quantities from a previous provider never leak across providers.
  const selectProvider = (id: WaterProviderId | null) => {
    setProviderId(id);
    setQuantities({});
  };

  const setQty = (productId: string, next: number) =>
    setQuantities((prev) => ({ ...prev, [productId]: next }));

  // ---- Cart computation ----
  const lines = useMemo(() => {
    if (!provider) return [];
    return provider.products
      .map((p) => {
        const qty = quantities[p.id] ?? 0;
        if (qty === 0) return null;
        const tier = bulkDiscount(qty);
        const gross = p.unitPrice * qty;
        const discount = tier ? (gross * tier.discountPercent) / 100 : 0;
        return { product: p, qty, gross, discount, net: gross - discount, tier };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [provider, quantities]);

  const subtotal = lines.reduce((s, l) => s + l.net, 0);
  const deliveryFee = provider && lines.length > 0 ? provider.deliveryFee : 0;
  const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE);
  const total = subtotal + deliveryFee + serviceFee;

  // ---- MOQ compliance ----
  // Every product line must meet its MOQ. Lines below MOQ are impossible by
  // stepper design, but we double-check defensively.
  const moqViolations = lines.filter((l) => l.qty > 0 && l.qty < l.product.minOrderQty);
  const hasItems = lines.length > 0;
  const moqMet = moqViolations.length === 0;
  const canPlaceOrder = hasItems && moqMet && dropoffAddress.trim().length > 4 && !loading;

  // ---- Submit ----
  const placeOrder = async () => {
    if (!provider || !canPlaceOrder) return;
    setLoading(true);

    // Build water-specific metadata (stored on the job's metadata JSON column).
    const metadata = {
      provider: provider.id,
      providerName: provider.name,
      providerType: provider.type,
      products: lines.map((l) => ({
        id: l.product.id,
        name: l.product.name,
        unitPrice: l.product.unitPrice,
        qty: l.qty,
        gross: l.gross,
        discount: l.discount,
        net: l.net,
        packSize: l.product.packSize ?? null,
        unit: l.product.unit,
      })),
      pricing: {
        subtotal,
        deliveryFee,
        serviceFee,
        serviceFeeRate: SERVICE_FEE_RATE,
        total,
        currency,
      },
      source: "water_order_form",
    };

    const firstLine = lines[0];
    const parcelDescription = `${provider.name} — ${lines
      .map((l) => `${l.product.shortName} ×${l.qty}`)
      .join(", ")}`;

    const res = await api.jobs.create({
      parcelDescription,
      parcelCategory: "water",
      parcelWeightKg: lines.reduce((s, l) => {
        // Rough weight estimate for courier vehicle matching.
        const w =
          l.product.unit === "gallon" ? 18.9 * l.qty : l.product.id.includes("2l") ? 2 * 6 * l.qty : 0.75 * 24 * l.qty;
        return s + w;
      }, 0),
      pickupAddress: `${provider.name} (${provider.type === "bottled" ? "Bottled Plant" : "Refill Station"})`,
      pickupNotes: `Water provider: ${provider.name}. Order placed via HornDeliver Order & Deliver.`,
      dropoffAddress: dropoffAddress.trim(),
      dropoffNotes: dropoffNotes.trim() || undefined,
      vehicleRequired: lines.reduce((s, l) => s + l.qty, 0) > 10 ? "pickup" : "motorcycle",
      contactPhone: contactPhone.trim() || undefined,
      customerOffer: total,
      mode: "order_deliver",
      metadata,
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || t("errors.generic"));
      return;
    }
    toast.success(t("waterOrder.placedSuccess"));
    navigate(`app/jobs/${res.data!.job.id}`);
  };

  // ---- Provider selection screen ----
  if (!provider) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 pb-32 sm:pb-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("app")}>
            <ArrowLeft className="h-4 w-4 rtl-flip" />
          </Button>
          <PageHeader
            title={t("waterOrder.title")}
            subtitle={t("waterOrder.providerSubtitle")}
          />
        </div>

        {/* Trust strip */}
        <div className="flex items-center justify-center gap-4 rounded-xl border border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> {t("waterOrder.verifiedProviders")}
          </span>
          <span className="flex items-center gap-1">
            <Truck className="h-3.5 w-3.5 text-primary" /> {t("waterOrder.fastDelivery")}
          </span>
          <span className="flex items-center gap-1">
            <Droplets className="h-3.5 w-3.5 text-primary" /> {t("waterOrder.qualityAssured")}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ProviderCard provider={PROVIDERS.ahla} onSelect={() => selectProvider("ahla")} />
          <ProviderCard provider={PROVIDERS.zamzam} onSelect={() => selectProvider("zamzam")} />
        </div>

        <p className="px-2 text-center text-xs text-muted-foreground">
          {t("waterOrder.moqExplainer")}
        </p>
      </div>
    );
  }

  // ---- Order form screen ----
  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-32 sm:pb-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => selectProvider(null)}
        >
          <ArrowLeft className="h-4 w-4 rtl-flip" />
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <img
            src={provider.logo}
            alt={provider.name}
            className="h-10 w-10 shrink-0 object-contain"
          />
          <div className="min-w-0">
            <p className="truncate font-bold leading-tight">{provider.name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-0.5">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                {provider.rating}
              </span>
              <span className="flex items-center gap-0.5">
                <Clock className="h-3 w-3" /> {provider.estMinutes}
              </span>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => selectProvider(null)}>
          {t("waterOrder.change")}
        </Button>
      </div>

      {/* Delivery address card */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <MapPin className="h-4 w-4 text-primary" /> {t("waterOrder.deliveryTo")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addr">{t("waterOrder.addressLabel")} *</Label>
            <Input
              id="addr"
              placeholder={t("waterOrder.addressPlaceholder")}
              value={dropoffAddress}
              onChange={(e) => setDropoffAddress(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes" className="flex items-center gap-1.5">
              <StickyNote className="h-3.5 w-3.5" /> {t("waterOrder.notesLabel")}
            </Label>
            <Input
              id="notes"
              placeholder={t("waterOrder.notesPlaceholder")}
              value={dropoffNotes}
              onChange={(e) => setDropoffNotes(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone" className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" /> {t("waterOrder.phoneLabel")}
            </Label>
            <Input
              id="phone"
              placeholder="+253 77 ..."
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Products */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 px-1 text-sm font-semibold">
          <ShoppingCart className="h-4 w-4 text-primary" /> {t("waterOrder.selectProducts")}
        </div>
        {provider.products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            qty={quantities[p.id] ?? 0}
            accent={provider.accent}
            onQtyChange={(next) => setQty(p.id, next)}
          />
        ))}
      </div>

      {/* MOQ compliance banner */}
      {hasItems && !moqMet && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{t("waterOrder.moqNotMet", { n: moqViolations.length })}</span>
        </div>
      )}
      {hasItems && moqMet && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>{t("waterOrder.moqMet")}</span>
        </div>
      )}

      {/* Order summary (desktop / inline) */}
      <Card className="hidden sm:block">
        <CardContent className="space-y-2 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <PackageCheck className="h-4 w-4 text-primary" /> {t("waterOrder.summary")}
          </div>
          <CartLines lines={lines} />
          <CartTotals
            subtotal={subtotal}
            deliveryFee={deliveryFee}
            serviceFee={serviceFee}
            total={total}
          />
        </CardContent>
      </Card>

      {/* Desktop place-order button */}
      <div className="hidden sm:block">
        <Button
          className="w-full"
          size="lg"
          disabled={!canPlaceOrder}
          onClick={placeOrder}
        >
          {loading ? t("waterOrder.placing") : t("waterOrder.placeOrder")}
          {!loading && canPlaceOrder ? ` — ${formatPrice(total)}` : ""}
        </Button>
        {!canPlaceOrder && !loading && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {!hasItems
              ? t("waterOrder.addAtLeastOne")
              : !moqMet
                ? t("waterOrder.fixMoqFirst")
                : t("waterOrder.fillAddressFirst")}
          </p>
        )}
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:hidden">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="min-w-0 flex-1">
            {hasItems ? (
              <>
                <p className="text-xs text-muted-foreground">{t("waterOrder.total")}</p>
                <p className="truncate text-lg font-bold text-primary">{formatPrice(total)}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {lines.length} {t("waterOrder.item")} · {t("waterOrder.serviceFeeIncluded")}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{t("waterOrder.emptyCart")}</p>
            )}
          </div>
          <Button
            className="shrink-0"
            disabled={!canPlaceOrder}
            onClick={placeOrder}
          >
            {loading ? "…" : t("waterOrder.placeOrder")}
          </Button>
        </div>
      </div>

      {/* Mobile expandable summary (above sticky bar) */}
      {hasItems && (
        <Card className="sm:hidden">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <PackageCheck className="h-4 w-4 text-primary" /> {t("waterOrder.summary")}
            </div>
            <CartLines lines={lines} />
            <CartTotals
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              serviceFee={serviceFee}
              total={total}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// Cart sub-components
// ============================================================

function CartLines({
  lines,
}: {
  lines: { product: WaterProduct; qty: number; net: number; discount: number; tier: { discountPercent: number } | null }[];
}) {
  if (lines.length === 0) {
    return <p className="py-2 text-center text-xs text-muted-foreground">—</p>;
  }
  return (
    <div className="space-y-1 border-y border-border py-2">
      {lines.map((l) => (
        <div key={l.product.id} className="flex items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate text-muted-foreground">
            {l.product.shortName} <span className="font-medium text-foreground">×{l.qty}</span>
            {l.tier && (
              <span className="ml-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                −{l.tier.discountPercent}%
              </span>
            )}
          </span>
          <span className="shrink-0 font-medium tabular-nums">{formatPrice(l.net)}</span>
        </div>
      ))}
    </div>
  );
}

function CartTotals({
  subtotal,
  deliveryFee,
  serviceFee,
  total,
}: {
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  total: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-sm">
      <Row label={t("waterOrder.subtotal")} value={formatPrice(subtotal)} />
      <Row label={t("waterOrder.deliveryFee")} value={formatPrice(deliveryFee)} />
      <Row label={t("waterOrder.serviceFee")} value={formatPrice(serviceFee)} muted />
      <div className="mt-1 flex items-center justify-between border-t border-border pt-2 text-base font-bold">
        <span>{t("waterOrder.total")}</span>
        <span className="text-primary">{formatPrice(total)}</span>
      </div>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${muted ? "text-muted-foreground" : ""}`}>{value}</span>
    </div>
  );
}
